import { Column } from 'react-table';
import SimpleDatatable from '../../../../../modules/components/datatable/SimpleDatatable';
import { useState } from 'react';
import { AddResultRemarkModal } from '../../modal/AddResultRemarkModal';
import TextWithLineBreaks from '../../../../../modules/components/text/TextWithLineBreaks';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import Avatar from '../../../../../modules/components/avatar/Avatar';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import Time from '../../../../../modules/components/time/Time';

export interface SummaryRow {
  key: string;
  field: React.ReactNode; // label
  render: () => React.ReactNode;
  reviewer?: React.ReactNode;
  canHaveRemark?: boolean;
  remarkKey?: string;
  action?: React.ReactNode;
}

interface Props {
  rows: SummaryRow[];
  data: any;
  refetch: () => void;
  title?: React.ReactNode;
  className?: string;
  hideHeader?: boolean;
}

export default function SummaryTable({
  rows,
  data,
  refetch,
  title,
  className,
  hideHeader = !rows.some((row) => row?.canHaveRemark),
}: Props) {
  const intl = useIntl();
  const { setToast } = useToast();
  const [showAddRemarkModal, setShowAddRemarkModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<SummaryRow>(null);
  const emDash = '—';

  function getRemark(key: string) {
    const remarkDetails = data?.result_remarks?.find((item) => item?.result_key === key);
    return remarkDetails
      ? {
          message: remarkDetails?.remark,
          user: remarkDetails?.user,
          url: remarkDetails?.url,
          time: remarkDetails?.created_at,
        }
      : undefined;
  }

  type ParsedSummaryRow = SummaryRow & {
    remark?: {
      message: string;
      user: string;
      url: string;
    };
  };

  const parsedRows: ParsedSummaryRow[] = rows.map((row) => ({
    ...row,
    remark: row?.remarkKey ? getRemark(row?.remarkKey) : undefined,
  }));
  const showReviewerColumn = parsedRows.some((row) => row?.reviewer);
  const showRemarkColumns = parsedRows.some((row) => row?.canHaveRemark);
  const showActionColumn = parsedRows.some((row) => row?.action);

  const columns: Column<ParsedSummaryRow>[] = [
    {
      Header: <>{title}</>,
      accessor: (row) => <div className='fw-bolder text-wrap'>{row?.field}</div>,
      minWidth: 100,
      id: 'header', // required if accessor is a function
    },
    {
      Header: hideHeader ? <></> : 'Value',
      accessor: (row) => <div className='text-wrap'>{row?.render()}</div>,
      minWidth: 200,
      id: 'value', // required if accessor is a function
    },
    ...(showReviewerColumn
      ? [
          {
            Header: hideHeader ? <></> : 'Reviewer',
            accessor: (row) => row?.reviewer,
            minWidth: 120,
            id: 'reviewer',
          },
        ]
      : []),
    ...(showActionColumn
      ? [
          {
            Header: hideHeader ? <></> : 'Action',
            accessor: (row) => row?.action,
            minWidth: 50,
            id: 'action',
          },
        ]
      : []),
    ...(showRemarkColumns
      ? [
          {
            Header: 'Remark',
            accessor: (row) =>
              row?.canHaveRemark &&
              (row?.remark?.message ? (
                <div className='d-flex gap-2 align-items-start  text-wrap'>
                  {row?.remark?.user && (
                    <Avatar
                      name={row.remark.user}
                      tooltipLabel={
                        <>
                          <span className='fw-bold me-2'>{row.remark.user}</span>
                          {row?.remark?.time && (
                            <span className='text-muted small'>
                              <Time date={new Date(row.remark.time)} format='MMM d, yyyy h:m aaa' />
                            </span>
                          )}
                        </>
                      }
                    />
                  )}
                  <div className='vstack'>
                    <span>
                      <TextWithLineBreaks text={row?.remark?.message} />
                    </span>
                    <span className='text-muted text-break' title='Source URL'>
                      <i
                        className='bi bi-link-45deg me-1 float-start'
                        style={{ marginTop: '3px' }}
                      ></i>
                      {row?.remark?.url}
                    </span>
                  </div>
                </div>
              ) : (
                <i
                  className={clsx('fa fa-edit', 'mx-2 cursor-pointer fs-1x')}
                  title={'Edit Remark'}
                  onClick={(e) => {
                    e.currentTarget.blur();
                    setShowAddRemarkModal(!showAddRemarkModal);
                    setSelectedRow(row);
                  }}
                />
              )),
            minWidth: 100,
            id: 'remark', // required if accessor is a function
          },
        ]
      : []),
  ];

  return (
    <>
      {selectedRow && (
        <AddResultRemarkModal
          data={data}
          selectedResult={selectedRow}
          show={showAddRemarkModal}
          handleClose={() => setShowAddRemarkModal(false)}
          handleRefetch={refetch}
        />
      )}

      {parsedRows.length > 0 && (
        <SimpleDatatable
          data={parsedRows}
          columns={columns}
          className={clsx('table-striped', className)}
        />
      )}

      {parsedRows.length === 0 && (
        <div className='text-center'>{intl.formatMessage({ id: 'COMMON.NO_RECORD_FOUND' })} </div>
      )}
    </>
  );
}
